import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useAuth } from '../context/AuthContext';

// Desktop has no email delivery (embedded, offline-first API) — forgot-password
// would be a dead end there, so the link is web-only. See CLAUDE.md's auth notes.
// NOT `!!VITE_API_URL` — that's also set for production *web* (cross-origin call
// to Cloud Run), which would hide this on web too. VITE_USE_HASH_ROUTER is the
// one flag that's genuinely desktop-only (see apps/desktop/.env.production).
const IS_DESKTOP_APP = import.meta.env.VITE_USE_HASH_ROUTER === 'true';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.login(email.trim(), password);
      login(res.accessToken, res.user);
      navigate('/invoices');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: 20, padding: '2.5rem', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {/* Links back to the marketing site (coreledger.in) — a separate
              deployment from this app (app.coreledger.in), see apps/landing.
              Web-only: the packaged desktop app has no marketing site to
              return to, same IS_DESKTOP_APP gate "Forgot password?" below
              already uses. */}
          {IS_DESKTOP_APP ? (
            <>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e3a5f' }}>CoreLedger Billing</h1>
            </>
          ) : (
            <a href="https://coreledger.in" style={{ textDecoration: 'none', display: 'block' }} title="Back to coreledger.in">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e3a5f' }}>CoreLedger Billing</h1>
            </a>
          )}
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.25rem' }}>Sign in to your account</p>
        </div>

        {flash && !error && (
          <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {flash}
          </div>
        )}
        {error && <div className="error-box">{error}</div>}

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'grid', gap: '0.3rem', marginBottom: '1rem', fontSize: '0.82rem', fontWeight: 500, color: '#374151' }}>
            Email address
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required autoFocus />
          </label>
          <label style={{ display: 'grid', gap: '0.3rem', marginBottom: '0.5rem', fontSize: '0.82rem', fontWeight: 500, color: '#374151' }}>
            Password
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required />
          </label>
          {!IS_DESKTOP_APP && (
            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
              <Link to="/forgot-password" style={{ color: '#2563eb', fontSize: '0.8rem', fontWeight: 500 }}>Forgot password?</Link>
            </div>
          )}
          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '0.75rem' }} disabled={loading}>
            {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Sign In →'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem', color: '#64748b' }}>
          New company?{' '}
          <Link to="/register" style={{ color: '#2563eb', fontWeight: 600 }}>Create account</Link>
        </p>
      </div>
    </div>
  );
}
